import { useState, useEffect } from "react";
import { Key, Check, Trash, ShieldCheck, Lightning, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const authAxios = axios.create({});

const PROVIDERS = [
  { id: "openai", name: "OpenAI", placeholder: "sk-...", docs: "https://platform.openai.com/api-keys", models: "GPT-4.1, GPT-4o" },
  { id: "anthropic", name: "Anthropic", placeholder: "sk-ant-...", docs: "https://console.anthropic.com/settings/keys", models: "Claude Sonnet, Opus" },
  { id: "google", name: "Google AI", placeholder: "AIza...", docs: "https://aistudio.google.com/apikey", models: "Gemini 2.0 Flash" },
];

export default function ApiKeysPage({ user }) {
  const [keys, setKeys] = useState({ openai: null, anthropic: null, google: null, provider: "openai", has_keys: false });
  const [inputs, setInputs] = useState({ openai: "", anthropic: "", google: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferred, setPreferred] = useState("openai");

  const isLifetime = user?.subscription === "lifetime_pro";
  const isAdmin = user?.is_admin;
  const canUse = isLifetime || isAdmin;

  useEffect(() => {
    if (!canUse) { setLoading(false); return; }
    authAxios.get(`${API}/settings/api-keys`).then(r => {
      setKeys(r.data);
      setPreferred(r.data.provider || "openai");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [canUse]);

  const handleSave = async (provider) => {
    if (!inputs[provider]) { toast.error("Enter an API key"); return; }
    setSaving(true);
    try {
      await authAxios.put(`${API}/settings/api-keys`, { [provider]: inputs[provider], preferred_provider: preferred });
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} key saved!`);
      setInputs(prev => ({ ...prev, [provider]: "" }));
      const r = await authAxios.get(`${API}/settings/api-keys`);
      setKeys(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save key");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (provider) => {
    try {
      await authAxios.delete(`${API}/settings/api-keys/${provider}`);
      toast.success("Key removed");
      const r = await authAxios.get(`${API}/settings/api-keys`);
      setKeys(r.data);
    } catch (e) { toast.error("Failed to remove"); }
  };

  const handlePreferred = async (value) => {
    setPreferred(value);
    try {
      await authAxios.put(`${API}/settings/api-keys`, { preferred_provider: value });
      toast.success(`Preferred provider set to ${value}`);
    } catch (e) { /* provider preference not critical */ }
  };

  if (!user?.email) {
    return <div className="max-w-lg mx-auto px-4 py-20 text-center"><p className="text-[#a3a3a3]">Sign in to manage API keys</p></div>;
  }

  if (!canUse) {
    const handleLogin = () => { window.location.href = "/billing"; };
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <Key size={48} weight="thin" className="mx-auto text-[#333] mb-4" />
        <h2 className="font-heading text-2xl text-white uppercase mb-2">BRING YOUR OWN API KEY</h2>
        <p className="text-sm text-[#a3a3a3] mb-4">This feature is available exclusively with the <strong className="text-[#ccff00]">Lifetime Pro</strong> plan ($149 one-time).</p>
        <p className="text-xs text-[#666] mb-6">Use your own OpenAI, Anthropic, or Google AI keys for unlimited AI-powered trade analysis with zero rate limits.</p>
        <Button onClick={handleLogin} className="h-10 px-6 btn-primary rounded-sm text-xs">VIEW LIFETIME PRO PLAN</Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
      <div className="flex items-center gap-3 mb-6">
        <Key size={24} weight="bold" className="text-[#ccff00]" />
        <div>
          <h1 className="font-heading text-2xl text-white uppercase" data-testid="api-keys-title">API KEYS</h1>
          <p className="text-sm text-[#a3a3a3]">Use your own AI provider keys for unlimited analysis</p>
        </div>
      </div>

      {/* How it works */}
      <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-4 mb-6">
        <p className="text-xs text-[#a3a3a3]"><strong className="text-[#ccff00]">How it works:</strong> Add your API key below. All AI features (Trade Analyzer, Roster Analysis, Waiver Wire, Draft Advice, Roster Optimizer) will use YOUR key first. If your key fails, we fall back to our key. You control your own usage and costs.</p>
      </div>

      {/* Preferred Provider */}
      <div className="border border-[#262626] bg-[#171717] p-5 mb-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">PREFERRED AI PROVIDER</p>
        <Select value={preferred} onValueChange={handlePreferred}>
          <SelectTrigger className="w-full h-10 bg-[#0a0a0a] border-[#262626] text-sm text-white" data-testid="preferred-provider">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#262626]">
            {PROVIDERS.map(p => (
              <SelectItem key={p.id} value={p.id} className="text-white hover:bg-[#262626]">
                {p.name} — {p.models}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Provider Keys */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 skeleton rounded-sm" />)}</div>
      ) : (
        <div className="space-y-4">
          {PROVIDERS.map(p => (
            <div key={p.id} className={`border bg-[#171717] p-5 ${preferred === p.id ? 'border-[#ccff00]/30' : 'border-[#262626]'}`} data-testid={`key-section-${p.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-white">{p.name}</h3>
                  {preferred === p.id && <span className="text-[8px] font-bold text-[#ccff00] bg-[#ccff00]/10 px-1.5 py-0.5">PREFERRED</span>}
                </div>
                <a href={p.docs} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#3b82f6] no-underline hover:underline">Get API Key</a>
              </div>
              
              {keys[p.id] ? (
                <div className="flex items-center justify-between bg-[#0a0a0a] border border-[#262626] px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Check size={14} weight="bold" className="text-[#4ade80]" />
                    <span className="font-mono text-xs text-[#a3a3a3]">{keys[p.id]}</span>
                  </div>
                  <button onClick={() => handleDelete(p.id)} className="text-[#666] hover:text-[#ff3b30]"><Trash size={14} weight="bold" /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input placeholder={p.placeholder} type="password" value={inputs[p.id]} onChange={(e) => setInputs(prev => ({ ...prev, [p.id]: e.target.value }))} className="h-10 bg-[#0a0a0a] border-[#262626] text-white text-sm font-mono placeholder:text-[#444]" data-testid={`key-input-${p.id}`} />
                  <Button onClick={() => handleSave(p.id)} disabled={saving} className="h-10 px-4 btn-primary rounded-sm text-xs shrink-0" data-testid={`save-key-${p.id}`}>SAVE</Button>
                </div>
              )}
              <p className="text-[10px] text-[#666] mt-2">Models: {p.models}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
